import { Inject, Injectable, Renderer2, RendererFactory2 } from "@angular/core";
import { Store } from "@ngrx/store";

import { Observable, merge, of, timer } from "rxjs";
import { filter, map, mapTo, take } from "rxjs/operators";
import { selectKeycloakInitialized } from "src/app/core/ngrx/ui";

import { WINDOW } from "src/app/core/services/window.service";
import { environment } from "src/environments/environment";
import { PwaService } from "./pwa.service";
interface NewWindow extends Window {
  consentManagerConfig: any;
}
@Injectable({
  providedIn: "root",
})
export class SnippetService {
  segmentScriptAdded = false;
  googleTagManagerScriptAdded = false;
  friendbuyScriptAdded = false;
  kcInitTimeout = 5000; // ms
  surveyRetries = 10;
  surveyDelay = 250; // ms
  surveyAnalyticsError = new Error("Analytics not initialized");
  surveyFlagName = "enable-surveys";
  private consentContainer: HTMLElement | null = null;

  private _renderer: Renderer2;

  private get renderer(): Renderer2 {
    if (!this._renderer) {
      this._renderer = this.rendererFactory.createRenderer(null, null);
    }
    return this._renderer;
  }

  constructor(
    private store$: Store,
    private rendererFactory: RendererFactory2,
    private pwa: PwaService,
    @Inject(WINDOW) private window: Window
  ) {}

  generateSnippets(): Observable<boolean> {
    const loadSegment =
      environment.segmentConfig.track && !this.segmentScriptAdded;
    const loadConsentManager = !(this.pwa.standalone && this.pwa.isInstalled);
    const kcInitializationObservableWithTimeout = merge(
      this.store$.select(selectKeycloakInitialized),
      timer(this.kcInitTimeout).pipe(mapTo(false))
    );

    if (!loadSegment) {
      return of(false);
    }

    return kcInitializationObservableWithTimeout.pipe(
      filter((init) => init !== null),
      take(1), // Observable must complete
      map((initialized) => {
        if (!initialized) {
          return true;
        }
        if (loadConsentManager) {
          this.enableConsentManager();
        }
        if (loadSegment) {
          this.addSegmentSnippet();
        }
        if (!this.googleTagManagerScriptAdded) {
          this.handleGoogleTagManager();
        }
        if (!this.friendbuyScriptAdded) {
          this.addFriendbuySnippet();
        }
        this.addFeatherySnippet();
        return true;
      })
    );
  }

  private addSegmentSnippet() {
    const analyticsScript = this.renderer.createElement("script");
    analyticsScript.type = "text/javascript";
    analyticsScript.async = true;
    analyticsScript.text = environment.segmentConfig.snippet;

    this.renderer.appendChild(this.window.document.head, analyticsScript);

    this.segmentScriptAdded = true;
  }

  private addFeatherySnippet() {
    const featheryScript = this.renderer.createElement("script");
    featheryScript.type = "text/javascript";
    featheryScript.async = true;
    featheryScript.src =
      "https://cdn.jsdelivr.net/npm/@feathery/react@latest/umd/index.js";

    this.renderer.appendChild(this.window.document.head, featheryScript);
  }

  private enableConsentManager() {
    const win = this.window as NewWindow;
    win.consentManagerConfig = () => ({
      ...environment.consentManagerInput,
      writeKey: environment.segmentConfig.writeKey,
      reloadPage: false,
    });
    const contentElement = this.renderer.createElement("div");
    contentElement.id = "consent";
    this.consentContainer = contentElement;
    const consentManagerScript = this.renderer.createElement("script");
    consentManagerScript.type = "text/javascript";
    this.renderer.setAttribute(
      consentManagerScript,
      "src",
      environment.consentManagerUrl
    );
    this.renderer.setAttribute(consentManagerScript, "defer", "true");

    this.renderer.appendChild(this.window.document.body, contentElement);
    this.renderer.appendChild(this.window.document.head, consentManagerScript);

    // detect that the container is now empty, and then remove it.
    const observer = new MutationObserver((mutations, obs) => {
      // If the container has no child nodes, assume the banner has been closed.
      if (contentElement.children.length === 0) {
        if (this.consentContainer) {
          this.renderer.removeChild(
            this.window.document.body,
            this.consentContainer
          );
          this.consentContainer = null;
        }
        // Stop observing once we've removed the container.
        obs.disconnect();
      } else {
        if (this.isMobile()) {
          this.setConsentManagerStylesForMobile();
        }
      }
    });

    observer.observe(contentElement, { childList: true, subtree: true });
  }

  private handleGoogleTagManager() {
    var gtmScript = this.renderer.createElement("script");
    gtmScript.type = "text/javascript";
    gtmScript.async = true;
    gtmScript.text = environment.googleTagManagerConfig.snippetHeader;
    this.renderer.appendChild(this.window.document.head, gtmScript);

    this.googleTagManagerScriptAdded = true;
  }

  private addFriendbuySnippet() {
    const friendbuyScript = this.renderer.createElement("script");
    friendbuyScript.type = "text/javascript";
    friendbuyScript.async = true;
    friendbuyScript.text = environment.friendbuyConfig.snippet;

    this.renderer.appendChild(this.window.document.head, friendbuyScript);

    this.friendbuyScriptAdded = true;
  }

  private isMobile(): boolean {
    return window.innerWidth <= 768; // Adjust the value as needed
  }

  private setConsentManagerStylesForMobile() {
    const contentChildElement = this.window.document.getElementsByClassName(
      "css-1lnj5rp"
    )[0];
    const consentElementButton = this.window.document.getElementsByClassName(
      "css-1c3imcp"
    )[0];
    if (contentChildElement && consentElementButton) {
      (contentChildElement as HTMLElement).setAttribute(
        "style",
        "display: flex; padding: 5px; padding-bottom:25px; flex-direction: column-reverse;margin-bottom:25px;"
      );
      (consentElementButton as HTMLElement).setAttribute(
        "style",
        "left: 45%; position: relative; transform: translateY(0);padding-bottom:10px"
      );
    }
  }
}
